import { createElement } from 'react';
import toast from 'react-hot-toast';
import {
  Heart,
  HeartBreak,
  MapPin,
  Info,
  CheckCircle,
  WarningCircle,
  BookmarkSimple,
  Copy,
} from '@phosphor-icons/react';

/**
 * Уведомления. Брендбук, раздел 8.
 *
 * Тип подбирается по смыслу, а не по успешности вызова: «удалено из избранного»
 * выполнилось, но достижением не является — значит нейтральный, без заливки.
 * Заливка остаётся двум крайним случаям: крупный успех и ошибка (8.2).
 *
 * Иконки — Phosphor с weight="fill" вместо эмодзи: эмодзи рендерятся системным
 * шрифтом, выглядят по-разному на iOS и Android и не подчиняются теме (8.1).
 */

// Длительности (брендбук 8.4). Ошибка держится дольше — её надо прочитать
const DURATION = {
  neutral: 2000,
  success: 2500,
  error: 4000,
};

const ICON_SIZE = 18;

/**
 * Готовые иконки для частых случаев — чтобы не импортировать Phosphor по вызову.
 *
 * Цвет привязан к иконке, а не к месту вызова: он зависит от смысла глифа,
 * и одна и та же иконка должна выглядеть одинаково везде (брендбук 8.3).
 * Служебные приглушены, знаковые сохраняют свой цвет — иначе сердце
 * «добавлено» серым неотличимо от разбитого «удалено».
 */
export const notifyIcons = {
  favoriteAdded: [Heart, 'var(--color-danger)'],
  favoriteRemoved: [HeartBreak, 'var(--color-text-hint)'],
  bookmark: [BookmarkSimple, 'var(--color-action-text)'],
  location: [MapPin, 'var(--color-text-secondary)'],
  info: [Info, 'var(--color-text-secondary)'],
  copy: [Copy, 'var(--color-text-secondary)'],
  done: [CheckCircle, 'var(--color-text-secondary)'],
  warning: [WarningCircle, 'var(--color-text-secondary)'],
};

/**
 * Принимает либо пару [иконка, цвет] из `notifyIcons`, либо голый компонент.
 * `fallbackColor` применяется, когда цвет не задан парой: на залитом тосте
 * глиф обязан быть цветом `on-semantic`, что бы ни пришло.
 *
 * Третий элемент пары — начертание, если `fill` не подходит: у пары
 * «добавлено / убрано» из одного глифа залитое означает добавленное,
 * контурное — снятое. Для отдельного глифа таких пар нет, там `fill`.
 */
const renderIcon = (icon, fallbackColor, forceColor) => {
  if (!icon) return undefined;

  const [Icon, ownColor, ownWeight] = Array.isArray(icon) ? icon : [icon, undefined, undefined];
  return createElement(Icon, {
    size: ICON_SIZE,
    weight: ownWeight || 'fill',
    color: forceColor || ownColor || fallbackColor,
  });
};

// Один тост на экране: новый заменяет предыдущий, а не встаёт в очередь
// (брендбук 8.5). Фиксированный id заставляет react-hot-toast переиспользовать
// уже открытый тост вместо создания второго
const TOAST_ID = 'app-toast';

/**
 * Нейтральное подтверждение — вариант по умолчанию.
 * Добавлено, удалено, скопировано.
 */
export const notify = (message, { icon, ...options } = {}) =>
  toast(message, {
    id: TOAST_ID,
    duration: DURATION.neutral,
    // Цвет глифа берётся из notifyIcons; служебные приглушены (брендбук 8.3)
    icon: renderIcon(icon, 'var(--color-text-secondary)'),
    ...options,
  });

/**
 * Крупный успех — то, что пользователь завершил: маршрут пройден,
 * заявка на розыгрыш принята. Единственное место зелёной заливки.
 */
notify.success = (message, { icon = CheckCircle, iconColor, ...options } = {}) =>
  toast.success(message, {
    id: TOAST_ID,
    duration: DURATION.success,
    // Заливка зелёная — глиф обязан быть контрастным ей, свой цвет иконки
    // не годится. По умолчанию on-semantic; iconColor нужен там, где
    // заливка светлая (в тёмной теме success осветлён и on-semantic
    // на нём почти чёрный)
    icon: renderIcon(icon, null, iconColor || 'var(--color-on-semantic)'),
    ...options,
  });

/**
 * Ошибка. Текст обязан говорить, что делать, а не «Ошибка» (брендбук 8.5).
 */
notify.error = (message, { icon = WarningCircle, ...options } = {}) =>
  toast.error(message, {
    id: TOAST_ID,
    duration: DURATION.error,
    icon: renderIcon(icon, null, 'var(--color-on-semantic)'),
    ...options,
  });

export default notify;
